import { useEffect, useMemo, useRef, useState } from "react";
import { toast } from "sonner";
import { FileText, Key, BookOpen, Folder, UploadCloud, Download, Trash2, Plus, X } from "lucide-react";
import api, { formatErr, fileUrl } from "../lib/api";
import { confirmDialog } from "../lib/confirm";
import { BRAND_COLORS } from "../lib/bookingStatus";
import { deDateMedium } from "../lib/dateFormat";

const CATEGORIES = [
  { key: "contract", label: "Verträge", icon: FileText },
  { key: "key",      label: "Schlüssel", icon: Key },
  { key: "manual",   label: "Handbücher", icon: BookOpen },
  { key: "other",    label: "Sonstiges", icon: Folder },
];
const CAT_MAP = Object.fromEntries(CATEGORIES.map((c) => [c.key, c]));
const MAX_MB = 25;

function bytesFmt(n) {
  if (!n && n !== 0) return "";
  if (n < 1024) return `${n} B`;
  if (n < 1024 * 1024) return `${Math.round(n / 1024)} KB`;
  return `${(n / (1024 * 1024)).toFixed(1)} MB`;
}

function UploadModal({ open, onClose, onSaved, customerId }) {
  const fileRef = useRef(null);
  const [file, setFile] = useState(null);
  const [title, setTitle] = useState("");
  const [category, setCategory] = useState("contract");
  const [notes, setNotes] = useState("");
  const [busy, setBusy] = useState(false);

  const reset = () => { setFile(null); setTitle(""); setCategory("contract"); setNotes(""); if (fileRef.current) fileRef.current.value = ""; };
  const close = () => { reset(); onClose(); };

  const onPick = (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    if (f.size > MAX_MB * 1024 * 1024) { toast.error(`Datei zu groß (max. ${MAX_MB} MB).`); return; }
    setFile(f);
    if (!title) setTitle(f.name.replace(/\.[^.]+$/, ""));
  };

  const save = async () => {
    if (!customerId) { toast.error("Bitte einen Kunden wählen."); return; }
    if (!file) { toast.error("Bitte eine Datei wählen."); return; }
    if (!title.trim()) { toast.error("Bitte einen Titel angeben."); return; }
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data: fref } = await api.post("/files/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      const { data: doc } = await api.post("/documents", {
        file_id: fref.id,
        title: title.trim(),
        category,
        notes: notes.trim() || null,
        customer_id: customerId,
      });
      toast.success("Dokument beim Kunden hinterlegt.");
      onSaved(doc);
      close();
    } catch (err) {
      toast.error(formatErr(err.response?.data?.detail) || err.message);
    } finally { setBusy(false); }
  };

  if (!open) return null;
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-navy/70 p-4" onClick={close}>
      <div className="bg-white w-full max-w-lg rounded-lg" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between px-5 py-4 border-b border-navy/10">
          <div>
            <div className="label-caps" style={{ color: BRAND_COLORS.gold }}>Neues Dokument</div>
            <h2 className="font-display text-2xl text-navy mt-1">Für Kunde hochladen</h2>
          </div>
          <button onClick={close} aria-label="Schließen"><X className="w-5 h-5 text-navy" /></button>
        </div>
        <div className="p-5 space-y-5">
          <div>
            <div className="label-caps mb-2">Datei</div>
            {file ? (
              <div className="flex items-center gap-3 border border-gold/40 bg-ivory/60 px-3 py-3">
                <FileText className="w-4 h-4" style={{ color: BRAND_COLORS.gold }} strokeWidth={1.5} />
                <div className="flex-1 min-w-0">
                  <div className="text-[13px] text-navy truncate">{file.name}</div>
                  <div className="text-[11px] text-navy-dark/60">{bytesFmt(file.size)}</div>
                </div>
                <button onClick={() => { setFile(null); if (fileRef.current) fileRef.current.value = ""; }} className="text-navy-dark/60 hover:text-navy">
                  <X className="w-4 h-4" />
                </button>
              </div>
            ) : (
              <label htmlFor="team-doc-upload-input"
                className="flex flex-col items-center justify-center gap-2 border border-dashed border-navy/25 hover:border-gold cursor-pointer bg-ivory/40 py-8">
                <UploadCloud className="w-6 h-6" style={{ color: BRAND_COLORS.gold }} strokeWidth={1.4} />
                <div className="label-caps">Datei wählen</div>
                <div className="text-[11px] text-navy-dark/50 italic">PDF, JPG, PNG, DOC · max. {MAX_MB} MB</div>
                <input id="team-doc-upload-input" ref={fileRef} type="file" className="hidden"
                  accept=".pdf,.doc,.docx,.xls,.xlsx,.png,.jpg,.jpeg,.txt" onChange={onPick} data-testid="team-doc-file-input" />
              </label>
            )}
          </div>
          <div>
            <label htmlFor="team-doc-title" className="label-caps block mb-2">Titel</label>
            <input id="team-doc-title" value={title} onChange={(e) => setTitle(e.target.value)}
              placeholder="z. B. Betreuungsvertrag 2026"
              data-testid="team-doc-title-input"
              className="w-full border border-navy/20 focus:border-gold focus:outline-none px-4 py-3 text-navy-dark" />
          </div>
          <div>
            <div className="label-caps mb-2">Kategorie</div>
            <div className="grid grid-cols-4 gap-2">
              {CATEGORIES.map((c) => {
                const Icon = c.icon;
                const active = category === c.key;
                return (
                  <button key={c.key} type="button"
                    data-testid={`team-doc-cat-${c.key}`}
                    onClick={() => setCategory(c.key)}
                    className={`flex flex-col items-center gap-1.5 py-3 border transition-colors ${active ? "border-gold bg-gold/5" : "border-navy/15 hover:border-navy/40"}`}>
                    <Icon className={`w-4 h-4 ${active ? "text-navy" : "text-navy-dark/60"}`} strokeWidth={1.5} />
                    <div className={`text-[11px] tracking-wider ${active ? "text-navy" : "text-navy-dark/70"}`}>{c.label}</div>
                  </button>
                );
              })}
            </div>
          </div>
          <div>
            <label htmlFor="team-doc-notes" className="label-caps block mb-2">Notiz <span className="lowercase text-navy-dark/50 italic">(optional)</span></label>
            <textarea id="team-doc-notes" rows={2} value={notes} onChange={(e) => setNotes(e.target.value)}
              data-testid="team-doc-notes-input"
              className="w-full border border-navy/20 focus:border-gold focus:outline-none px-4 py-3 text-navy-dark leading-relaxed" />
          </div>
        </div>
        <div className="px-5 py-4 border-t border-navy/10 flex justify-end gap-3">
          <button onClick={close} disabled={busy} className="text-[12px] tracking-wider uppercase text-navy-dark/70 hover:text-navy px-4 py-2.5">Abbrechen</button>
          <button onClick={save} disabled={busy} data-testid="team-doc-save-btn"
            className="text-ivory px-6 py-3 text-[11px] tracking-[0.18em] uppercase hover:opacity-90 disabled:opacity-60"
            style={{ background: BRAND_COLORS.navy }}>
            {busy ? "Wird gespeichert…" : "Beim Kunden speichern"}
          </button>
        </div>
      </div>
    </div>
  );
}

export default function TeamDocuments() {
  const [customers, setCustomers] = useState([]);
  const [customerId, setCustomerId] = useState("");
  const [docs, setDocs] = useState(null);
  const [filter, setFilter] = useState("all");
  const [uploadOpen, setUploadOpen] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get("/team/customers");
        setCustomers(data);
        if (data[0]) setCustomerId(data[0].id);
      } catch (err) { console.error("customers load failed:", err); }
    })();
  }, []);

  useEffect(() => {
    if (!customerId) return;
    setDocs(null);
    (async () => {
      try {
        const { data } = await api.get("/documents", { params: { customer_id: customerId } });
        setDocs(data);
      } catch (err) {
        toast.error(formatErr(err.response?.data?.detail) || err.message);
        setDocs([]);
      }
    })();
  }, [customerId]);

  const grouped = useMemo(() => {
    const g = {};
    for (const d of (docs || [])) (g[d.category] = g[d.category] || []).push(d);
    return g;
  }, [docs]);
  const filtered = useMemo(() => {
    if (!docs) return [];
    return filter === "all" ? docs : (grouped[filter] || []);
  }, [docs, filter, grouped]);

  const del = async (d) => {
    if (!(await confirmDialog({
      title: "Dokument löschen?",
      description: `„${d.title}" wird unwiderruflich entfernt und ist danach für den Kunden nicht mehr sichtbar.`,
      confirmText: "Löschen",
      variant: "danger",
    }))) return;
    try {
      await api.delete(`/documents/${d.id}`);
      setDocs((cur) => cur.filter((x) => x.id !== d.id));
      toast.success("Gelöscht.");
    } catch (err) {
      toast.error(formatErr(err.response?.data?.detail) || err.message);
    }
  };

  return (
    <div className="max-w-[1200px] mx-auto px-6 md:px-10 py-10 md:py-14" data-testid="team-documents">
      <div className="mb-8">
        <div className="label-caps mb-3">Weissblom Team</div>
        <h1 className="font-display text-4xl md:text-5xl text-navy">Dokumente je Kunde</h1>
        <p className="text-[14px] text-navy-dark/70 mt-3 max-w-2xl">
          Laden Sie Verträge, Schlüsselcodes und Handbücher direkt in den Bereich des Kunden hoch. Der Kunde sieht sie sofort in „Meine Dokumente".
        </p>
      </div>

      <div className="bg-white border border-navy/10 p-4 md:p-5 mb-6 flex flex-col md:flex-row md:items-center gap-4">
        <div className="flex-1">
          <label htmlFor="team-doc-customer" className="label-caps block mb-2">Kunde</label>
          <select id="team-doc-customer" data-testid="team-doc-customer-select"
            value={customerId} onChange={(e) => setCustomerId(e.target.value)}
            className="w-full border border-navy/20 focus:border-gold focus:outline-none px-4 py-3 text-navy-dark bg-white">
            {customers.map((c) => <option key={c.id} value={c.id}>{c.name || c.email}</option>)}
          </select>
        </div>
        <button
          onClick={() => setUploadOpen(true)}
          data-testid="team-doc-add-btn"
          disabled={!customerId}
          className="inline-flex items-center gap-2 text-ivory px-5 py-3 text-[11px] tracking-[0.18em] uppercase hover:opacity-90 disabled:opacity-40"
          style={{ background: BRAND_COLORS.navy }}>
          <Plus className="w-4 h-4" /> Dokument hinzufügen
        </button>
      </div>

      {/* Category filter */}
      <div className="flex gap-2 overflow-x-auto pb-2 mb-6">
        <button onClick={() => setFilter("all")}
          className={`whitespace-nowrap px-4 py-2 text-[12px] tracking-wider border ${filter === "all" ? "border-navy bg-navy text-ivory" : "border-navy/20 text-navy-dark"}`}
          style={filter === "all" ? { background: BRAND_COLORS.navy } : {}}>
          Alle {docs ? `(${docs.length})` : ""}
        </button>
        {CATEGORIES.map((c) => {
          const count = (grouped[c.key] || []).length;
          const Icon = c.icon;
          return (
            <button key={c.key} onClick={() => setFilter(c.key)}
              className={`inline-flex items-center gap-2 whitespace-nowrap px-4 py-2 text-[12px] tracking-wider border ${filter === c.key ? "border-navy bg-navy text-ivory" : "border-navy/20 text-navy-dark"}`}
              style={filter === c.key ? { background: BRAND_COLORS.navy } : {}}>
              <Icon className="w-3.5 h-3.5" /> {c.label} {count > 0 && <span className={filter === c.key ? "text-ivory/70" : "text-navy-dark/50"}>· {count}</span>}
            </button>
          );
        })}
      </div>

      {docs === null ? (
        <div className="bg-white border border-navy/10 p-8 text-center label-caps">Wird geladen …</div>
      ) : filtered.length === 0 ? (
        <div className="bg-ivory/60 border border-dashed border-navy/20 p-10 text-center">
          <Folder className="w-8 h-8 mx-auto mb-3" style={{ color: BRAND_COLORS.gold }} strokeWidth={1.3} />
          <div className="font-display text-xl text-navy">Keine Dokumente</div>
          <p className="text-[13px] text-navy-dark/60 mt-2">Über den Button oben ein Dokument beim Kunden hinterlegen.</p>
        </div>
      ) : (
        <ul className="space-y-2" data-testid="team-doc-list">
          {filtered.map((d) => {
            const cat = CAT_MAP[d.category] || CAT_MAP.other;
            const Icon = cat.icon;
            return (
              <li key={d.id} className="bg-white border border-navy/10 p-4 flex items-center gap-3" data-testid={`team-doc-row-${d.id}`}>
                <div className="w-10 h-10 rounded-full bg-ivory flex items-center justify-center shrink-0">
                  <Icon className="w-4 h-4 text-navy-dark/70" strokeWidth={1.5} />
                </div>
                <div className="flex-1 min-w-0">
                  <div className="text-[14px] text-navy font-medium truncate">{d.title}</div>
                  <div className="text-[11.5px] text-navy-dark/55 truncate">
                    {cat.label} · {deDateMedium(d.created_at)}
                    {d.uploaded_by_name ? ` · von ${d.uploaded_by_name}` : ""}
                    {d.notes ? ` · ${d.notes}` : ""}
                  </div>
                </div>
                <a href={fileUrl(d.file_id)} download={d.original_filename || d.title} target="_blank" rel="noreferrer"
                  className="w-9 h-9 flex items-center justify-center border border-navy/15 hover:border-gold" title="Herunterladen">
                  <Download className="w-4 h-4 text-navy" strokeWidth={1.5} />
                </a>
                <button onClick={() => del(d)}
                  data-testid={`team-doc-delete-${d.id}`}
                  className="w-9 h-9 flex items-center justify-center border border-navy/15 hover:border-red-600 hover:text-red-600 text-navy-dark/70" title="Löschen">
                  <Trash2 className="w-4 h-4" strokeWidth={1.5} />
                </button>
              </li>
            );
          })}
        </ul>
      )}

      <UploadModal open={uploadOpen} onClose={() => setUploadOpen(false)} customerId={customerId}
        onSaved={(doc) => setDocs((cur) => [doc, ...(cur || [])])} />
    </div>
  );
}
